
import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { UserWithProfile } from "@/types/analytics";
import { Loader2, UserPlus, Shield, User } from "lucide-react";
import { assignAdminRole, assignTesterRole } from "@/services/analyticsService";

export default function UserRoleManager() {
  const [email, setEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const queryClient = useQueryClient();
  
  // Fetch all users and their roles
  const { data: users, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      // Get all users
      const { data: { users }, error: authError } = await supabase.auth.admin.listUsers();
      
      if (authError) throw authError;
      
      // Get all user roles
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('*');
      
      if (rolesError) throw rolesError;
      
      // Combine users with their roles
      return users.map(user => ({
        ...user,
        roles: roles.filter(role => role.user_id === user.id)
      })) as UserWithProfile[];
    },
  });

  const handleInviteUser = async () => {
    if (!email) {
      toast.error("Please enter an email address");
      return;
    }
    
    setIsInviting(true);
    
    try {
      const { data, error } = await supabase.auth.admin.inviteUserByEmail(email);
      
      if (error) throw error;
      
      toast.success(`Invitation sent to ${email}`);
      setEmail("");
      queryClient.invalidateQueries({ queryKey: ['users'] });
      
    } catch (error) {
      console.error("Error inviting user:", error);
      toast.error("Failed to invite user");
    } finally {
      setIsInviting(false);
    }
  };

  const handleSetAdmin = async (userId: string) => {
    await assignAdminRole(userId);
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const handleSetTester = async (userId: string) => {
    await assignTesterRole(userId);
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };
  
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">Error loading users: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>User Management</CardTitle>
        <CardDescription>
          Invite new users and manage their roles
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 mb-6">
          <Input
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="max-w-sm"
          />
          <Button onClick={handleInviteUser} disabled={isInviting}>
            {isInviting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Inviting...
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Invite User
              </>
            )}
          </Button>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users && users.length > 0 ? (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {user.roles && user.roles.length > 0 ? (
                          user.roles.map((role) => (
                            <Badge key={role.id} variant="outline">
                              {role.role}
                            </Badge>
                          ))
                        ) : (
                          <Badge variant="outline">user</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetAdmin(user.id)}
                        >
                          <Shield className="h-4 w-4 mr-1" />
                          Make Admin
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetTester(user.id)}
                        >
                          <User className="h-4 w-4 mr-1" />
                          Make Tester
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
